import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  DollarSign, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Search,
  Download,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorPayroll = () => {
  const { getAuthHeaders } = useAuth();
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedVendors, setExpandedVendors] = useState(new Set());
  const [selectedPayables, setSelectedPayables] = useState(new Set());
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPayrollData();
  }, [statusFilter]);

  const fetchPayrollData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_URL}/api/reports/vendor-payroll?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        setPayrollData(await response.json());
      } else {
        toast.error('Failed to load payroll data');
      }
    } catch (error) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const toggleVendorExpand = (vendorId) => {
    const newExpanded = new Set(expandedVendors);
    if (newExpanded.has(vendorId)) {
      newExpanded.delete(vendorId);
    } else {
      newExpanded.add(vendorId);
    }
    setExpandedVendors(newExpanded);
  };

  const togglePayableSelection = (payableId) => {
    const newSelected = new Set(selectedPayables);
    if (newSelected.has(payableId)) {
      newSelected.delete(payableId);
    } else {
      newSelected.add(payableId);
    }
    setSelectedPayables(newSelected);
  };

  const selectAllForVendor = (vendor) => {
    const newSelected = new Set(selectedPayables);
    const vendorPayableIds = vendor.payables.map(p => p.payable_id);
    
    const allSelected = vendorPayableIds.every(id => selectedPayables.has(id));
    
    if (allSelected) {
      vendorPayableIds.forEach(id => newSelected.delete(id));
    } else {
      vendorPayableIds.forEach(id => newSelected.add(id));
    }
    
    setSelectedPayables(newSelected);
  };

  const selectAllApproved = () => {
    const newSelected = new Set();
    payrollData?.vendors.forEach(vendor => {
      vendor.payables
        .filter(p => p.status === 'approved')
        .forEach(p => newSelected.add(p.payable_id));
    });
    setSelectedPayables(newSelected);
  };

  const handleMarkPaid = async () => {
    if (selectedPayables.size === 0) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/vendor-payroll/mark-paid-batch`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          payable_ids: Array.from(selectedPayables)
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Marked ${result.modified_count} payables as paid`);
        setSelectedPayables(new Set());
        setShowPayDialog(false);
        fetchPayrollData();
      } else {
        toast.error('Failed to process payments');
      }
    } catch (error) {
      toast.error('Failed to process payments');
    } finally {
      setProcessing(false);
    }
  };

  const getSelectedTotal = () => {
    let total = 0;
    payrollData?.vendors.forEach(vendor => {
      vendor.payables.forEach(p => {
        if (selectedPayables.has(p.payable_id)) {
          total += p.total;
        }
      });
    });
    return total;
  };

  const filteredVendors = payrollData?.vendors.filter(vendor => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      vendor.vendor_name?.toLowerCase().includes(query) ||
      vendor.vendor_number?.toLowerCase().includes(query) ||
      vendor.email?.toLowerCase().includes(query)
    );
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-payroll">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-green-600" />
            Vendor Payroll
          </h1>
          <p className="text-muted-foreground">Manage vendor payments and run payroll</p>
        </div>
        
        <div className="flex items-center gap-3">
          {selectedPayables.size > 0 && (
            <Button onClick={() => setShowPayDialog(true)} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              Pay Selected ({selectedPayables.size}) - {formatCurrency(getSelectedTotal())}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Due</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(payrollData?.summary?.total_due)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {formatCurrency(payrollData?.summary?.total_pending)}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ready to Pay</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(payrollData?.summary?.total_approved)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vendors</p>
                <p className="text-2xl font-bold text-purple-600">
                  {payrollData?.summary?.total_vendors}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outstanding</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
                <SelectItem value="approved">Ready to Pay</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={selectAllApproved}>
              Select All Approved
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vendor List */}
      <Card>
        <CardHeader>
          <CardTitle>Vendors Awaiting Payment</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredVendors.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No vendors awaiting payment</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredVendors.map((vendor) => {
                const isExpanded = expandedVendors.has(vendor.vendor_id);
                const allSelected = vendor.payables.every(p => selectedPayables.has(p.payable_id));
                const someSelected = vendor.payables.some(p => selectedPayables.has(p.payable_id));
                
                return (
                  <div key={vendor.vendor_id} className="bg-white">
                    {/* Vendor Row */}
                    <div 
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleVendorExpand(vendor.vendor_id)}
                    >
                      <Checkbox
                        checked={allSelected}
                        className={someSelected && !allSelected ? 'opacity-50' : ''}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAllForVendor(vendor);
                        }}
                      />
                      
                      <div className="flex-shrink-0">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{vendor.vendor_name}</p>
                          {vendor.vendor_number && (
                            <span className="text-xs font-mono text-muted-foreground">
                              {vendor.vendor_number}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {vendor.email} • {vendor.phone || 'No phone'}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-muted-foreground">Payables</p>
                          <p className="font-semibold">{vendor.payable_count}</p>
                        </div>
                        {vendor.pending_count > 0 && (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                            {vendor.pending_count} pending
                          </Badge>
                        )}
                        {vendor.approved_count > 0 && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                            {vendor.approved_count} approved
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Due</p>
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(vendor.total_amount)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Expanded Payables */}
                    {isExpanded && (
                      <div className="bg-gray-50 border-t px-4 pb-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10"></TableHead>
                              <TableHead>Work Order</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vendor.payables.map((payable) => (
                              <TableRow key={payable.payable_id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedPayables.has(payable.payable_id)}
                                    onCheckedChange={() => togglePayableSelection(payable.payable_id)}
                                    disabled={payable.status === 'pending'}
                                  />
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                  {payable.work_order_id}
                                </TableCell>
                                <TableCell>{formatDate(payable.created_at)}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={
                                      payable.status === 'approved'
                                        ? 'bg-green-50 text-green-700'
                                        : 'bg-yellow-50 text-yellow-700'
                                    }
                                  >
                                    {payable.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {formatCurrency(payable.total)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pay Confirmation Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>
              You are about to mark {selectedPayables.size} payables as paid.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">Total Amount</p>
              <p className="text-3xl font-bold text-green-700">
                {formatCurrency(getSelectedTotal())}
              </p>
            </div>
            
            <p className="mt-4 text-sm text-muted-foreground">
              This will mark the selected payables as paid and update vendor records.
              Make sure you have processed the actual payments before confirming.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkPaid} 
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? 'Processing...' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorPayroll;
